import { useMemo, useState } from 'react'
import {
  CLEARANCE_MIN,
  axisLimits,
  axisTravels,
  buildSkeleton,
  computeClearance,
  type ClearancePair,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card, Table, Tabs, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead } from '@/sim/parts'
import {
  AxisLimitsSection,
  EnvelopeSection,
  ManualSection,
  WorkspaceSection,
  demandFrom,
} from './clearance-parts'

/**
 * Freigang — wo die Maschine sich selbst im Weg steht.
 *
 * ZWEI SPALTEN, UND DIE ZWEITE IST DIE INTERESSANTE. „Gleichzeitig" ist der
 * Abstand, wenn beide Bauteile in derselben Phase stehen — das passiert
 * wirklich. „Zeitversetzt" ist der Abstand über alle Phasenpaare — das
 * passiert, sobald der Gang von der gerechneten Kadenz abweicht.
 *
 * Ein Paar mit 40 mm gleichzeitig und −5 mm zeitversetzt ist im Normalbetrieb
 * unauffällig und beim ersten Stolpern ein Treffer. Diese Zeilen sind eigens
 * gekennzeichnet; sie sind der Grund, dass diese Seite mehr ist als eine
 * Wiederholung dessen, was man im Bild sieht.
 *
 * DIE PRÜFUNG LÄUFT AUF KNOPFDRUCK und nicht bei jeder Änderung: sie kostet
 * rund 60 ms, das ist zu viel für einen Schieberegler und zu wenig, um dafür
 * einen Nebenläufer aufzumachen.
 *
 * VIER REGISTER, VIER FRAGEN. Sie sind nicht dasselbe, und sie zusammenzulegen
 * hiesse, die Antworten zu vermischen:
 *
 *   Paaranalyse  — kommt sich der Aufbau IM GANG ins Gehege?
 *   Achsgrenzen  — wie weit darf EINE Achse, wenn alles andere ruht?
 *   Arbeitsraum  — wohin kommt eine KETTE, wenn alle ihre Achsen mitgehen?
 *   Pose         — steht DIESE eine Stellung, die ich gerade meine?
 *
 * Die ersten drei rechnen über den Zyklus bzw. über die Winkelgrenzen; die
 * vierte ist die einzige, in der ein Mensch die Stellung setzt. Sie hält
 * ausserdem die Bühne an: solange eine Handpose steht, zeigt die Figur sie und
 * nicht den Gang.
 */

/** Millimeter aus Metern, mit Vorzeichen. */
const mm = (value: number): string =>
  `${value >= 0 ? '' : '−'}${Math.abs(value * 1000)
    .toFixed(1)
    .replace('.', ',')}`

const phase = (value: number): string => `${Math.round(value * 100)} %`

type Filter = 'alle' | 'eng' | 'still' | 'takt'

function classify(pair: ClearancePair): 'breach' | 'latent' | 'outOfStep' | 'clear' {
  if (pair.synchronous < CLEARANCE_MIN) return 'breach'
  if (pair.asynchronous >= CLEARANCE_MIN) return 'clear'
  return pair.contralateral ? 'outOfStep' : 'latent'
}

export function ClearancePage() {
  const { model, state, simulation, manual, setManual } = useSimulation()
  const [ran, setRan] = useState(false)
  /*
    `null` heisst „noch nicht gewählt". Die Gruppe wird dann aus dem Ergebnis
    bestimmt: gibt es Verletzungen, stehen die vorn; gibt es nur stille Fälle,
    stehen die vorn. Eine feste Vorbelegung würde bei einem sauberen Gang eine
    leere Tabelle zeigen und den Eindruck erwecken, die Prüfung habe nichts
    gefunden — dabei hat sie sieben Paare gefunden, nur in der anderen Gruppe.
  */
  const [chosen, setFilter] = useState<Filter | null>(null)

  const skeleton = useMemo(() => buildSkeleton(model), [model])

  /*
    Die Rechnung hängt an Modell, Gang und Bodenkontakt. `ran` schaltet sie
    frei — solange sie nicht gelaufen ist, steht hier nichts. Wer danach einen
    Gelenkpunkt verschiebt, bekommt ein neues Ergebnis, weil sich die
    Abhängigkeiten ändern; das ist gewollt und kostet die 60 ms erneut.
  */
  const result = useMemo(
    () => (ran ? computeClearance(model, skeleton, simulation.gait, simulation.ground) : null),
    [ran, model, skeleton, simulation.gait, simulation.ground],
  )

  /*
    Die Achsgrenzen laufen OHNE Knopfdruck. Sie kosten rund 60 ms und hängen nur
    am Modell — nicht am Gang, nicht am Bodenkontakt, nicht an einem
    Schieberegler. Sie einmal je Modellstand zu rechnen ist die richtige
    Häufigkeit; sie hinter einen Knopf zu legen hiesse, drei Register leer zu
    zeigen, die alle auf derselben Zahl aufbauen.
  */
  const travel = useMemo(() => axisTravels(model, skeleton, undefined, Object.keys(state.off)), [model, skeleton, state.off])

  const limitRows = useMemo(
    () => axisLimits(model, travel.travels, demandFrom(simulation.gait.angles), Object.keys(state.off)),
    [model, travel, simulation.gait.angles],
  )

  const filter: Filter =
    chosen ??
    (!result
      ? 'eng'
      : result.breaches > 0
        ? 'eng'
        : result.latent > 0
          ? 'still'
          : result.outOfStep > 0
            ? 'takt'
            : 'alle')

  const rows = result
    ? result.pairs.filter((pair) => {
        const kind = classify(pair)
        if (filter === 'alle') return true
        if (filter === 'eng') return kind === 'breach'
        if (filter === 'takt') return kind === 'outOfStep'
        return kind === 'latent'
      })
    : []

  const pairs = (
    <div className="stack">
      {!result && (
        <Alert tone="info" title="Noch nicht gerechnet">
          Die Prüfung vergleicht rund hundert Bauteilpaare über 120 Phasen, für den zeitversetzten
          Fall jede Phase gegen jede. Das dauert einen Augenblick und läuft deshalb auf Knopfdruck
          statt bei jeder Änderung eines Schiebereglers.
        </Alert>
      )}

      {result && (
        <>
          <div className="metrics">
            <Metric
              label="Engster Abstand"
              value={`${mm(result.tightest)} mm`}
              note="gleichzeitig, über alle Paare"
              warn={result.tightest < CLEARANCE_MIN}
            />
            <Metric
              label="Zu eng"
              value={result.breaches}
              note={`Paare unter ${(CLEARANCE_MIN * 1000).toFixed(0)} mm im Normalbetrieb`}
              warn={result.breaches > 0}
            />
            <Metric
              label="Nur zeitversetzt zu eng"
              value={result.latent}
              note="unauffällig im Takt, kritisch bei jeder Abweichung"
              warn={result.latent > 0}
            />
            <Metric
              label="Bei Taktverlust"
              value={result.outOfStep}
              note="linkes gegen rechtes Glied — kein Aufbaufehler"
            />
            <Metric
              label="Geprüft"
              value={`${result.pairs.length} Paare`}
              note={`${result.bodies} Körper · ${result.samples} Phasen`}
            />
          </div>

          {result.breaches > 0 && (
            <Alert tone="warning" title="Der Gang durchdringt sich selbst">
              {result.breaches} Paare unterschreiten den geforderten Freigang von{' '}
              {(CLEARANCE_MIN * 1000).toFixed(0)} mm schon im Normalbetrieb. Das engste ist{' '}
              <b>
                {result.pairs[0].labelA} gegen {result.pairs[0].labelB}
              </b>{' '}
              mit {mm(result.pairs[0].synchronous)} mm bei {phase(result.pairs[0].synchronousPhase)}{' '}
              des Zyklus. Der gerechnete Gang spreizt die Schultern nicht ab; die Vorlage tut das in
              ihrer Grundstellung mit zehn Grad und schreibt dazu, ohne das berühre die Hand den
              Oberschenkel. Das ist derselbe Befund, hier über den ganzen Zyklus gemessen statt in
              einer Stellung.
            </Alert>
          )}

          <Card
            title="Bauteilpaare"
            eyebrow={`geordnet nach dem engsten gleichzeitigen Abstand · Freigang ${(
              CLEARANCE_MIN * 1000
            ).toFixed(0)} mm`}
            actions={
              <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                {(
                  [
                    ['eng', `Zu eng (${result.breaches})`],
                    ['still', `Nur zeitversetzt (${result.latent})`],
                    ['takt', `Bei Taktverlust (${result.outOfStep})`],
                    ['alle', `Alle (${result.pairs.length})`],
                  ] as [Filter, string][]
                ).map(([key, label]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={filter === key ? 'primary' : 'ghost'}
                    onClick={() => setFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            }
          >
            {rows.length === 0 ? (
              <p className="page-lead">
                Kein Paar in dieser Gruppe.
              </p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Bauteil</Th>
                    <Th>gegen</Th>
                    <Th align="right">Gleichzeitig</Th>
                    <Th align="right">bei</Th>
                    <Th align="right">Zeitversetzt</Th>
                    <Th align="right">bei</Th>
                    <Th>Befund</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((pair) => {
                    const kind = classify(pair)
                    return (
                      <tr key={`${pair.a}|${pair.b}`}>
                        <Td>{pair.labelA}</Td>
                        <Td>{pair.labelB}</Td>
                        <Td align="right" numeric>
                          <span className={pair.synchronous < CLEARANCE_MIN ? 'cell-warn' : ''}>
                            {mm(pair.synchronous)} mm
                          </span>
                        </Td>
                        <Td align="right" numeric>
                          <span className="cell-muted">{phase(pair.synchronousPhase)}</span>
                        </Td>
                        <Td align="right" numeric>
                          <span className={pair.asynchronous < CLEARANCE_MIN ? 'cell-warn' : ''}>
                            {mm(pair.asynchronous)} mm
                          </span>
                        </Td>
                        <Td align="right" numeric>
                          <span className="cell-muted">
                            {phase(pair.phaseA)} / {phase(pair.phaseB)}
                          </span>
                        </Td>
                        <Td>
                          {kind === 'breach' && <Badge tone="danger">zu eng</Badge>}
                          {kind === 'latent' && <Badge tone="warning">nur zeitversetzt</Badge>}
                          {kind === 'outOfStep' && <Badge tone="neutral">bei Taktverlust</Badge>}
                          {kind === 'clear' && <span className="cell-muted">frei</span>}
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  )

  return (
    <div className="stack">
      <PageHead
        title="Freigang"
        actions={
          <Button size="sm" variant="primary" onClick={() => setRan(true)}>
            {result ? 'Paare neu rechnen' : 'Paarprüfung starten'}
          </Button>
        }
      />

      <Tabs
        aria-label="Grenzlagen"
        items={[
          {
            id: 'paare',
            label: 'Paaranalyse',
            count: result?.pairs.length,
            content: pairs,
          },
          {
            id: 'achsen',
            label: 'Achsgrenzen',
            count: travel.travels.length / 2,
            content: <AxisLimitsSection travel={travel} rows={limitRows} />,
          },
          {
            id: 'umhuellung',
            label: 'Umhüllung',
            content: <EnvelopeSection model={model} skeleton={skeleton} />,
          },
          {
            id: 'raum',
            label: 'Arbeitsraum',
            content: (
              <WorkspaceSection model={model} skeleton={skeleton} travels={travel.travels} />
            ),
          },
          {
            id: 'pose',
            label: 'Pose von Hand',
            count: manual ? Object.keys(manual).length : undefined,
            content: (
              <ManualSection
                model={model}
                skeleton={skeleton}
                travels={travel.travels}
                pose={manual}
                setPose={setManual}
              />
            ),
          },
        ]}
      />

      <Card title="Reichweite der Prüfung">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Das Kapselmodell</span>
            <p className="page-lead">
              Jedes Bauteil ist eine Strecke zwischen zwei Gelenkpunkten plus ein Radius. Die Radien
              stammen aus den Schalenmassen des Aufbaus und sind grosszügig gewählt. Eine Kapsel,
              die etwas dicker ist als das Bauteil, meldet einen Konflikt zu früh — eine, die zu
              dünn ist, meldet ihn gar nicht. Für eine Freigangsprüfung ist das die richtige
              Richtung des Irrtums.
            </p>
          </div>
          <div>
            <span className="eyebrow">Sie folgt den Punkten</span>
            <p className="page-lead">
              Die Kapseln hängen an denselben Gelenkpunkten wie die Rechnung: ein verschobener
              Gelenkpunkt ändert die Kapseln mit.
            </p>
          </div>
          <div>
            <span className="eyebrow">Was fehlt</span>
            <p className="page-lead">
              Eine Kapsel ist keine Bauteilhülle. Ein L-förmiger Träger, ein Kabelbaum oder ein
              Steckerkopf sind darin nicht abgebildet. Für eine belastbare Aussage über den Freigang
              braucht es Hüllkörper je Baugruppe aus dem CAD; diese Prüfung findet die groben Fälle
              und die, die von der Bewegung kommen — und das sind erfahrungsgemäss die, die man
              übersieht.
            </p>
          </div>
          <div>
            <span className="eyebrow">Zeitversetzt heisst nicht überall dasselbe</span>
            <p className="page-lead">
              Linkes und rechtes Bein sind im Gang fest gekoppelt — sie sind immer eine halbe
              Periode auseinander, und der gerechnete Gang enthält diesen Versatz bereits. Dass der
              rechte Fuss zeitversetzt dort steht, wo der linke später steht, ist kein
              Entwurfsfehler. Diese Paare sind deshalb eigens ausgewiesen: bei ihnen
              beantwortet der zeitversetzte Abstand die Frage, was bei einem Taktverlust passiert,
              und nicht die, ob der Aufbau sich selbst im Weg steht.
            </p>
          </div>
          <div>
            <span className="eyebrow">Eine Achse für sich sagt zu wenig</span>
            <p className="page-lead">
              Die Achsgrenzen fahren immer nur EINE Achse; alles andere ruht. Die rechte Hüfte darf
              danach bis 4,75° adduzieren, die linke ebenso — beide zugleich treffen sich mit den
              Schienbeinen, rund 90&nbsp;mm ineinander. Keine der beiden Achsen liegt dabei ausserhalb
              ihres Bereichs, und keine Zahl in der Achsgrenzentabelle kann diesen Fall zeigen. Für
              solche Kombinationen gibt es das Register <b>Pose von Hand</b>.
            </p>
          </div>
          <div>
            <span className="eyebrow">Geometrie, nicht Statik</span>
            <p className="page-lead">
              Alle vier Register prüfen ausschliesslich, ob sich Körper durchdringen. Ob eine
              Stellung STEHT — ob der Schwerpunkt über der Stützfläche liegt, ob die Momente
              aufbringbar sind —, steht hier nicht. Eine Grenzlage kann kollisionsfrei und trotzdem
              unmöglich sein; die Antwort darauf geben Analyse und Antriebe.
            </p>
          </div>
          <div>
            <span className="eyebrow">Nachbarn werden übersprungen</span>
            <p className="page-lead">
              Oberschenkel und Schienbein teilen sich die Knieachse und berühren einander immer.
              Solche Paare stehen nicht in der Liste.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
